/**
 * Site search — Pagefind-backed, EN + RU only.
 *
 * The markup is server-rendered by `searchHtml()` in src/templates/layout.ts, so the
 * field exists and is focusable before this runs; this module only adds behaviour.
 * Pagefind and its WASM are lazy-loaded on first use — nothing is fetched until
 * somebody actually searches.
 *
 * One component, two presentations (ported from 301-ui, which shipped this):
 * a dropdown under the field on desktop, a full-screen sheet on mobile. There is no
 * second markup — CSS switches on `body.search-open-mobile`.
 *
 * Pagefind picks the index language itself from `<html lang>`, so unlike 301-ui there
 * is no per-locale bundle path and no result URL re-prefixing to do here.
 */

interface Strings {
  idle: string;
  noResults: string;
  navigate: string;
  open: string;
  close: string;
}

interface PagefindData {
  url: string;
  meta?: { title?: string };
  excerpt?: string;
}

interface PagefindModule {
  search: (q: string) => Promise<{ results: { data: () => Promise<PagefindData> }[] }>;
  init?: () => Promise<void>;
}

const MAX_RESULTS = 6;
const DEBOUNCE_MS = 160;
const MOBILE_MAX = 1023;

const root = document.querySelector<HTMLElement>('[data-search]');
if (root) init(root);

function init(wrap: HTMLElement): void {
  const field = wrap.querySelector<HTMLElement>('.search__field')!;
  const input = wrap.querySelector<HTMLInputElement>('.search__input')!;
  const palette = wrap.querySelector<HTMLElement>('.search__palette')!;
  const results = wrap.querySelector<HTMLElement>('.search__results')!;
  const toggle = wrap.querySelector<HTMLButtonElement>('.search__toggle')!;
  const closeBtn = wrap.querySelector<HTMLButtonElement>('.search__close')!;

  const t: Strings = JSON.parse(wrap.dataset.searchStrings || '{}');
  const home = wrap.parentElement!;
  const homeNext = wrap.nextElementSibling;

  let engine: PagefindModule | null = null;
  let loadingEngine: Promise<PagefindModule> | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let active = -1;
  let links: HTMLAnchorElement[] = [];
  let query = '';
  let token = 0; // discards out-of-order async renders

  /**
   * Built at runtime so esbuild cannot resolve it at build time: the Pagefind runtime
   * is a deploy artifact under /pagefind/, not a module we bundle.
   */
  function loadEngine(): Promise<PagefindModule> {
    if (engine) return Promise.resolve(engine);
    if (loadingEngine) return loadingEngine;
    const path = ['', 'pagefind', 'pagefind.js'].join('/');
    loadingEngine = import(/* webpackIgnore: true */ path)
      .then(async (m: PagefindModule) => {
        await m.init?.();
        engine = m;
        return m;
      })
      .catch((err) => {
        loadingEngine = null;
        throw err;
      });
    return loadingEngine;
  }

  const isMobile = (): boolean => window.matchMedia(`(max-width: ${MOBILE_MAX}px)`).matches;

  /**
   * Pagefind derives result URLs from file paths, and build.ts writes any slug without a
   * trailing slash as a flat file — so `/docs/syntax` comes back as `/docs/syntax.html`.
   * The site links clean URLs everywhere; a redirect hop here would be our own doing.
   */
  function cleanUrl(url: string): string {
    return url.replace(/\/index\.html$/, '/').replace(/\.html$/, '');
  }

  function escapeHtml(v: string): string {
    return v.replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
  }

  function setPanel(html: string): void {
    results.innerHTML = html;
    links = [];
    active = -1;
  }

  function open(): void {
    if (!palette.hidden) return;
    palette.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-expanded', 'true');
    if (!results.innerHTML) setPanel(`<p class="search__empty">${escapeHtml(t.idle)}</p>`);
  }

  function close(): void {
    palette.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-expanded', 'false');
    active = -1;
  }

  /**
   * [CRITICAL] The wrapper is moved into <body> for the mobile sheet and moved back on
   * close. `.nav` is `position: sticky` with `backdrop-filter: blur(12px)`, and
   * backdrop-filter — exactly like transform — makes the element a containing block for
   * `position: fixed` descendants. Left inside the nav, the sheet gets clipped to the nav
   * box: the input shows and the results do not. This reads as a CSS bug and is not one.
   */
  function openMobile(): void {
    document.body.appendChild(wrap);
    document.body.classList.add('search-open-mobile');
    open();
    input.focus();
  }

  function closeMobile(): void {
    document.body.classList.remove('search-open-mobile');
    close();
    if (wrap.parentElement === document.body) home.insertBefore(wrap, homeNext);
  }

  function closeAny(): void {
    if (document.body.classList.contains('search-open-mobile')) closeMobile();
    else close();
  }

  function render(items: PagefindData[]): void {
    if (!items.length) {
      setPanel(`<p class="search__empty">${escapeHtml(t.noResults.replace('{q}', query))}</p>`);
      return;
    }
    results.innerHTML = items
      .map((d) => {
        const title = escapeHtml(d.meta?.title || d.url);
        // Pagefind excerpts are already-sanitised HTML carrying <mark> hits.
        return `<a class="search__result" href="${escapeHtml(cleanUrl(d.url))}" role="option">
  <span class="search__result-title">${title}</span>
  <span class="search__result-excerpt">${d.excerpt || ''}</span>
</a>`;
      })
      .join('');
    links = Array.from(results.querySelectorAll<HTMLAnchorElement>('.search__result'));
    active = -1;
  }

  async function run(q: string): Promise<void> {
    const mine = ++token;
    query = q;
    if (!q.trim()) {
      setPanel(`<p class="search__empty">${escapeHtml(t.idle)}</p>`);
      return;
    }
    try {
      const pf = await loadEngine();
      const res = await pf.search(q);
      if (mine !== token) return;
      const data = await Promise.all(res.results.slice(0, MAX_RESULTS).map((r) => r.data()));
      if (mine !== token) return;
      render(data);
    } catch {
      if (mine === token) setPanel(`<p class="search__empty">${escapeHtml(t.noResults.replace('{q}', q))}</p>`);
    }
  }

  function move(delta: number): void {
    if (!links.length) return;
    active = (active + delta + links.length) % links.length;
    links.forEach((a, i) => a.classList.toggle('is-active', i === active));
    links[active].scrollIntoView({ block: 'nearest' });
  }

  input.addEventListener('input', () => {
    open();
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void run(input.value), DEBOUNCE_MS);
  });

  input.addEventListener('focus', () => { if (!isMobile()) open(); });

  wrap.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); links[active].click(); }
    else if (e.key === 'Escape') { closeAny(); input.blur(); }
  });

  toggle.addEventListener('click', () => {
    if (isMobile()) openMobile();
    else { open(); input.focus(); }
  });

  closeBtn.addEventListener('click', () => { closeAny(); input.blur(); });

  // The mobile sheet covers the viewport, so only the desktop dropdown needs this.
  document.addEventListener('click', (e) => {
    if (document.body.classList.contains('search-open-mobile')) return;
    if (!wrap.contains(e.target as Node)) close();
  });

  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      if (isMobile()) openMobile();
      else { open(); input.focus(); input.select(); }
    }
  });

  // Leaving mobile width with the sheet open would strand the wrapper in <body>.
  window.matchMedia(`(max-width: ${MOBILE_MAX}px)`).addEventListener('change', (e) => {
    if (!e.matches && document.body.classList.contains('search-open-mobile')) closeMobile();
  });

  // The field is a real input before this runs; only now is it wired.
  field.classList.add('is-ready');
}
